import { useEffect, useMemo, useState } from "react";
import { ArrowRight, AlertTriangle, CheckCircle2, CreditCard, Loader2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useSubscription, Plan } from "@/hooks/useSubscription";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/useAuth";
import { useChurch } from "@/hooks/useChurch";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

const PLAN_FEATURES = [
  "Ministry team management",
  "Fund based accounting tools",
  "Unlimited financial transactions",
  "Role based user access",
  "Giving contribution statements",
];

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

export default function SubscriptionPage() {
  const { plans, subscription, loading, isActive, validatePromoCode } = useSubscription();
  const { isChurchAdmin } = usePermissions();
  const { loading: authLoading, user } = useAuth();
  const { profile } = useChurch();
  const queryClient = useQueryClient();

  const canManageBilling = isChurchAdmin;
  const hasChurchProfile = !!profile?.id;

  const [processingPlanId, setProcessingPlanId] = useState<string | null>(null);
  const [promoInput, setPromoInput] = useState("");
  const [promoValidating, setPromoValidating] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState<{ id: string; code: string; discount_percent: number } | null>(null);
  const [promoError, setPromoError] = useState("");
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [isYearly, setIsYearly] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [embeddedOpen, setEmbeddedOpen] = useState(false);
  const [embeddedClientSecret, setEmbeddedClientSecret] = useState<string | null>(null);

  const YEARLY_DISCOUNT = 15;
  const currentPlan = subscription?.plans;
  const inactivePlanCtaLabel = "Subscribe plan";
  const subscriptionPeriodEndDate = subscription?.current_period_end ? new Date(subscription.current_period_end) : null;
  const subscriptionPeriodEndValid = !!subscriptionPeriodEndDate && !Number.isNaN(subscriptionPeriodEndDate.getTime());
  const hasFutureSubscriptionAccess =
    !!subscription &&
    (
      subscription.status === "active" ||
      subscription.status === "trialing" ||
      (subscription.status === "canceled" && subscriptionPeriodEndValid && subscriptionPeriodEndDate > new Date())
    );

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoValidating(true);
    setPromoError("");
    const result = await validatePromoCode(promoInput);
    if (result) {
      setAppliedPromo(result);
      setPromoError("");
    } else {
      setAppliedPromo(null);
      setPromoError("Invalid or expired promo code");
    }
    setPromoValidating(false);
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput("");
    setPromoError("");
  };

  const openEmbeddedCheckout = async (plan: Plan) => {
    if (!profile?.id) {
      toast.error("Church profile is still linking. Please wait a moment and try again.");
      return;
    }

    if (!stripePromise) {
      toast.error("Stripe publishable key is missing. Set VITE_STRIPE_PUBLISHABLE_KEY and try again.");
      return;
    }

    setProcessingPlanId(plan.id);
    const { data, error } = await supabase.functions.invoke("stripe-create-embedded-checkout", {
      body: {
        church_id: profile.id,
        plan_id: plan.id,
        customer_email: user?.email ?? null,
        return_url: `${window.location.origin}/subscription`,
      },
    });

    setProcessingPlanId(null);

    if (error || data?.error || !data?.client_secret) {
      const errorMessage =
        data?.error ||
        (typeof error?.message === "string" ? error.message : "") ||
        "Failed to create embedded checkout.";

      if (/404|not found|edge function/i.test(errorMessage)) {
        toast.error("Embedded checkout function is not deployed yet. Deploy stripe-create-embedded-checkout and try again.");
      } else {
        toast.error(errorMessage);
      }
      return;
    }

    setEmbeddedClientSecret(data.client_secret);
    setEmbeddedOpen(true);
  };

  const getDiscountedPrice = (price: number) => {
    let totalDiscount = isYearly ? YEARLY_DISCOUNT : 0;
    if (appliedPromo) totalDiscount += appliedPromo.discount_percent;
    if (totalDiscount === 0) return null;
    return price * (1 - totalDiscount / 100);
  };

  const getDisplayPrice = (price: number) => {
    const discounted = getDiscountedPrice(price);
    if (isYearly) {
      const yearlyBase = price * 12;
      const yearlyFinal = discounted !== null ? discounted * 12 : yearlyBase;
      return { base: yearlyBase, final: yearlyFinal, hasDiscount: discounted !== null };
    }
    return { base: price, final: discounted ?? price, hasDiscount: discounted !== null };
  };

  const sortedPlans = useMemo(() => {
    return [...plans].sort((a, b) => {
      const aLimit = a.max_members ?? Number.MAX_SAFE_INTEGER;
      const bLimit = b.max_members ?? Number.MAX_SAFE_INTEGER;
      return aLimit - bLimit;
    });
  }, [plans]);

  useEffect(() => {
    if (sortedPlans.length === 0) return;
    if (selectedPlanId && sortedPlans.some((plan) => plan.id === selectedPlanId)) return;

    const currentPlanInList = currentPlan?.id
      ? sortedPlans.find((plan) => plan.id === currentPlan.id)
      : null;

    setSelectedPlanId(currentPlanInList?.id ?? sortedPlans[0].id);
  }, [sortedPlans, selectedPlanId, currentPlan?.id]);

  // Embedded checkout redirects back here (onComplete does not fire when return_url is set),
  // so persist the new subscription from the Stripe session before relying on the webhook.
  useEffect(() => {
    if (!profile?.id) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") !== "success") return;

    const sessionId = params.get("session_id");
    window.history.replaceState({}, document.title, window.location.pathname);

    const syncAfterCheckout = async () => {
      const { data, error } = await supabase.functions.invoke("stripe-sync-subscription", {
        body: { church_id: profile.id, session_id: sessionId },
      });

      if (error || data?.error) {
        toast.error(data?.error || "Subscription sync failed. Please refresh.");
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ["subscription", profile.id] });
      toast.success("Subscription activated! Welcome aboard.");
    };

    syncAfterCheckout();
  }, [profile?.id, queryClient]);

  useEffect(() => {
    if (!subscription) {
      setIsYearly(false);
      return;
    }

    const periodStart = new Date(subscription.current_period_start);
    const periodEnd = new Date(subscription.current_period_end);
    const periodDays = Math.round((periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24));
    setIsYearly(periodDays > 60);
  }, [subscription?.current_period_start, subscription?.current_period_end, subscription?.status]);

  const selectedPlan = sortedPlans.find((plan) => plan.id === selectedPlanId) ?? null;
  const selectedDisplay = selectedPlan ? getDisplayPrice(selectedPlan.price) : null;
  const currentSubscriptionIsYearly = (() => {
    if (!subscription) return false;
    const periodStart = new Date(subscription.current_period_start);
    const periodEnd = new Date(subscription.current_period_end);
    const periodDays = Math.round((periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24));
    return periodDays > 60;
  })();
  const selectedIsCurrent =
    !!selectedPlan &&
    currentPlan?.id === selectedPlan.id &&
    hasFutureSubscriptionAccess &&
    isYearly === currentSubscriptionIsYearly;

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-pulse text-muted-foreground">Loading subscription...</div>
      </div>
    );
  }

  if (!canManageBilling && !isActive) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center space-y-4 max-w-md mx-auto">
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
          <CreditCard className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-heading font-semibold text-foreground">Subscription Required</h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Your church doesn't have an active subscription yet. Please contact your church
          administrator to set up a billing plan so your team can access all features.
        </p>
      </div>
    );
  }

  const showPromoSection = canManageBilling && (!subscription || subscription.status === "canceled");

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <CreditCard className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Subscription</h1>
          <p className="text-sm text-muted-foreground">Manage your church plan and billing cycle.</p>
        </div>
      </div>

      {!hasChurchProfile && (
        <section className="py-6 border-b border-amber-300/60 bg-amber-50/70 space-y-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
            <div>
              <p className="font-medium text-foreground">Still linking your church profile</p>
              <p className="text-sm text-muted-foreground">
                Please wait a moment and refresh this page. Billing starts as soon as your church profile is fully linked.
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="py-6 border-b border-border space-y-4">
        <div className="flex flex-col items-center justify-center gap-4 text-center">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-foreground mb-1">
              {hasFutureSubscriptionAccess ? "Change Plan" : "Choose a Plan"}
            </h2>
            <p className="text-sm text-muted-foreground">All plans include the same core features. Pick based on your church size.</p>
          </div>
          <div className="mx-auto inline-flex items-center rounded-full border border-[#dce5f0] bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setIsYearly(false)}
              aria-pressed={!isYearly}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                !isYearly ? "bg-[#1e3a8a] text-white" : "text-slate-600 hover:text-slate-900"
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setIsYearly(true)}
              aria-pressed={isYearly}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                isYearly ? "bg-[#1e3a8a] text-white" : "text-slate-600 hover:text-slate-900"
              )}
            >
              Yearly
              <span className="ml-2 inline-flex items-center rounded-full bg-[#dff3e8] px-1.5 py-0.5 text-[11px] font-semibold text-[#2a9a62]">
                Save {YEARLY_DISCOUNT}%
              </span>
            </button>
          </div>
        </div>

        <div className="mt-4 mx-auto w-full max-w-5xl overflow-hidden rounded-2xl border-2 border-[#1e3a8a]">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {sortedPlans.map((plan, index) => {
              const isSelected = selectedPlan?.id === plan.id;
              const memberNumber = plan.max_members ? plan.max_members.toLocaleString() : "5,000";
              const isSecondColumnMobile = index % 2 === 1;
              const isSecondRowMobile = index >= 2;
              const isNotFirstDesktop = index > 0;

              return (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => setSelectedPlanId(plan.id)}
                  className={cn(
                    "flex cursor-pointer flex-col items-center justify-center px-4 py-3 text-center transition-all duration-150",
                    "border-[#1e3a8a]",
                    isSecondColumnMobile ? "border-l" : "border-l-0",
                    isSecondRowMobile ? "border-t" : "border-t-0",
                    "md:border-t-0",
                    isNotFirstDesktop ? "md:border-l" : "md:border-l-0",
                    isSelected
                      ? "bg-[#1e3a8a] text-white"
                      : "bg-white text-foreground hover:bg-[#eaf0ff] hover:text-[#1e3a8a]"
                  )}
                  aria-pressed={isSelected}
                >
                  <span className="inline-flex items-center justify-center gap-1.5 text-center">
                    <Users className={cn("h-3.5 w-3.5", isSelected ? "text-white" : "text-[#1e3a8a]")} />
                    <span className="text-xl font-semibold leading-tight tracking-tight">{memberNumber}</span>
                    <span className={cn("text-xs font-normal leading-tight", isSelected ? "text-white/90" : "text-muted-foreground")}>
                      members
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {selectedPlan && selectedDisplay && (
          <div className="grid grid-cols-1 items-start gap-4 pt-6 md:grid-cols-[320px_minmax(0,1fr)]">
            <div className="space-y-2 text-center">
              <p className="min-h-7 whitespace-nowrap text-2xl font-semibold text-foreground leading-none">
                {selectedDisplay.hasDiscount ? (
                  <>
                    <span className="mr-2 text-base font-normal text-muted-foreground line-through">${selectedDisplay.base.toFixed(0)}</span>
                    ${selectedDisplay.final.toFixed(0)}
                  </>
                ) : (
                  <>${selectedDisplay.base.toFixed(0)}</>
                )}
                <span className="ml-1 text-sm font-normal text-muted-foreground">/{isYearly ? "yr" : "mo"}</span>
              </p>

              {isYearly && selectedDisplay.hasDiscount && (
                <p className="text-xs text-muted-foreground">
                  <span className="line-through">${selectedDisplay.base.toFixed(0)}/year</span> billed annually
                  <span className="ml-2 font-semibold text-emerald-600">
                    Save ${(selectedDisplay.base - selectedDisplay.final).toFixed(0)}
                  </span>
                </p>
              )}

              {showPromoSection && canManageBilling && !appliedPromo && (
                <button
                  type="button"
                  onClick={() => setShowPromoInput((prev) => !prev)}
                  className="mx-auto block text-sm font-light text-muted-foreground/80 hover:text-foreground"
                >
                  {showPromoInput ? "Hide Promo Code" : "Enter Promo Code"}
                </button>
              )}

              {showPromoSection && canManageBilling && appliedPromo && (
                <div className="mx-auto flex w-fit items-center gap-3 whitespace-nowrap">
                  <span className="text-sm font-light text-success">
                    {appliedPromo.discount_percent}% discount applied
                  </span>
                  <Button variant="ghost" size="sm" onClick={handleRemovePromo} className="h-auto p-0 text-xs font-light text-muted-foreground hover:text-foreground">
                    Remove
                  </Button>
                </div>
              )}

              {showPromoSection && canManageBilling && showPromoInput && !appliedPromo && (
                <div className="mx-auto w-56 space-y-2 text-left">
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="XXX-XXX-XXXX"
                      value={promoInput}
                      onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(""); }}
                      className="uppercase placeholder:text-muted-foreground/35 focus:placeholder:text-transparent"
                      maxLength={30}
                      onKeyDown={(e) => e.key === "Enter" && handleApplyPromo()}
                    />
                    <Button variant="outline" size="sm" onClick={handleApplyPromo} disabled={promoValidating || !promoInput.trim()}>
                      {promoValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                  {promoError && <p className="text-xs text-destructive">{promoError}</p>}
                </div>
              )}

              {canManageBilling && (
                <div className="space-y-3">
                  <Button
                    className={cn(
                      "h-12 w-56 rounded-xl px-6 text-sm mx-auto",
                      selectedIsCurrent
                        ? "bg-[#e5e7eb] text-[#6b7280] hover:bg-[#e5e7eb]"
                        : "bg-[#1e3a8a] text-white hover:bg-[#1e3a8a]/90"
                    )}
                    variant={selectedIsCurrent ? "secondary" : "default"}
                    disabled={selectedIsCurrent || processingPlanId === selectedPlan.id}
                    onClick={() => openEmbeddedCheckout(selectedPlan)}
                  >
                    {processingPlanId === selectedPlan.id ? (
                      <span className="inline-flex w-full items-center justify-center">Processing...</span>
                    ) : selectedIsCurrent ? (
                      <span className="inline-flex w-full items-center justify-center">Current Plan</span>
                    ) : (
                      <span className="inline-flex w-full items-center justify-center">
                        {hasFutureSubscriptionAccess ? "Switch Plan" : inactivePlanCtaLabel}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </span>
                    )}
                  </Button>
                </div>
              )}
            </div>

            <div className="mx-auto w-fit space-y-3 text-left md:mx-0 md:w-auto md:pl-1">
              {PLAN_FEATURES.map((feature) => (
                <p key={`${selectedPlan.id}-${feature}`} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-[#1e3a8a]" />
                  {feature}
                </p>
              ))}
            </div>
          </div>
        )}
      </section>

      <Dialog
        open={embeddedOpen}
        onOpenChange={(open) => {
          setEmbeddedOpen(open);
          if (!open) {
            setEmbeddedClientSecret(null);
            if (profile?.id) {
              queryClient.invalidateQueries({ queryKey: ["subscription", profile.id] });
            }
          }
        }}
      >
        <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-[560px] h-[88vh] max-h-[88vh] p-0 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto px-2 pb-2">
            {embeddedClientSecret && stripePromise ? (
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{
                  clientSecret: embeddedClientSecret,
                  onComplete: async () => {
                    if (profile?.id) {
                      await supabase.functions.invoke("stripe-sync-subscription", {
                        body: { church_id: profile.id },
                      });
                      await queryClient.invalidateQueries({ queryKey: ["subscription", profile.id] });
                    }
                    toast.success("Checkout completed. Subscription updated.");
                    setEmbeddedOpen(false);
                    setEmbeddedClientSecret(null);
                  },
                }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            ) : (
              <div className="py-10 text-center text-sm text-muted-foreground">Loading checkout...</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
